import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import apiClient from '../services/apiClient';
import StakeTable, { Column } from './GameEngine/StakeTable';

// ─── Types ───────────────────────────────────────────────────────────────────
interface ProfileData {
  id: number; name: string; email: string; password2?: string;
  balance: string; total_deposit: string; total_withdrawal: string;
  total_bets?: number; total_bet_amount?: string; total_win_amount?: string;
  created?: string; referalcode?: string; ip_address?: string; geo?: string;
}
interface ReferralData {
  f1: number; f2: number; f3: number; f4: number; f5: number; f6: number;
  total_invitations: number;
}
interface Transaction {
  id: number; amount: string; status?: string; req_type?: string;
  transaction_date?: string; utr_number?: string; payment_method?: string;
  txn_id?: string; type?: string; round_id?: string; game_id?: number;
  is_cancel?: number; created_at?: string;
}
interface CurrencyStat {
  currency: string;
  balance: string;
  total_deposit: string;
  total_withdrawal: string;
  total_bets: number;
  total_bet_amount: string;
  total_win_amount: string;
}
interface UserDetailsResponse {
  profile: ProfileData;
  currencies?: CurrencyStat[];
  referral: ReferralData;
  transactions: { data: Transaction[]; total: number; page: number; limit: number };
}

// ─── Status Badge ─────────────────────────────────────────────────────────────
const statusCfg: Record<string, string> = {
  accepted: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
  rejected:  'bg-red-500/10 border-red-500/20 text-red-400',
  pending:   'bg-amber-500/10 border-amber-500/20 text-amber-400',
  win:       'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
  bet:       'bg-sky-500/10 border-sky-500/20 text-sky-400',
};
const Tag: React.FC<{ label?: string }> = ({ label }) => {
  const key = label?.toLowerCase() || 'pending';
  const cls = statusCfg[key] || 'bg-slate-700 border-slate-600 text-slate-400';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-bold border ${cls}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {label || '—'}
    </span>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────
const UserProfile: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [activeTab, setActiveTab] = useState('deposits');
  const [subTab, setSubTab]       = useState('all');
  const [page, setPage]           = useState(1);
  const [selectedCurrency, setSelectedCurrency] = useState<string>('');
  const limit = 20;

  // ── Single query that re-fetches when tab / subTab / page changes ──────────
  const { data, isLoading, isFetching, refetch } = useQuery<UserDetailsResponse>({
    queryKey: ['userProfile', id, activeTab, subTab, page],
    queryFn: async () => {
      const res = await apiClient.get(
        `/admin/getUserDetails/${id}?tab=${activeTab}&status=${subTab}&page=${page}&limit=${limit}`
      );
      return res.data.data as UserDetailsResponse;
    },
    enabled: !!id,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  const profile = data?.profile;
  const referral = data?.referral;
  const txData = data?.transactions;
  const totalPages = Math.max(1, Math.ceil((txData?.total ?? 0) / limit));

  const currenciesList = data?.currencies || [];
  const activeCurrency = selectedCurrency || (currenciesList[0]?.currency ?? '');
  const activeCurrencyStat = currenciesList.find((c: CurrencyStat) => c.currency === activeCurrency);

  const getGatewayDisplayLabel = (name: string | undefined): string => {
    if (!name) return 'Manual';
    const labelMap: { [key: string]: string } = {
      'manual_bank': 'Bank Details',
      'manual_upi_wallet': 'UPI / Wallet',
    };
    return labelMap[name.toLowerCase()] || name;
  };

  const getMethodLabel = (provider: string | undefined): string => {
    if (!provider) return 'N/A';
    const labelMap: { [key: string]: string } = {
      'bdpay': 'BD Pay',
      'provider2': 'Wallet',
      'wallet': 'Wallet',
      'bkash': 'bKash',
      'manual': 'Manual',
      'manual_qr': 'Manual QR',
      'manual_upi': 'Manual UPI',
      'manualb_bank': 'Manual Bank',
    };
    return labelMap[provider.toLowerCase()] || provider;
  };

  // ── Columns: Deposits ───────────────────────────────────────────────────────
  const depositColumns: Column[] = [
    { key: 'id',               label: '#',      render: (_v, _r, i) => (page - 1) * limit + i + 1 },
    { key: 'amount',           label: 'Amount', render: (val) => <span className="font-bold text-white tabular-nums">{Number(val).toFixed(2)}</span> },
    { key: 'currency',         label: 'Currency', render: (_, item: any) => <span className="font-semibold text-slate-300">{(item.currency || item.transaction_type || 'INR').toUpperCase()}</span> },
    {
      key: 'gateway_name',
      label: 'Gateway Name',
      render: (_, item: any) => {
        const gName = item.gateway_name || item.payment_method || 'Manual';
        if (gName && gName !== 'Manual') return gName;
        const provider = item.payment_provider || item.transaction_type || '';
        if (provider.startsWith('manual')) {
          const parts = provider.split('_');
          if (parts.length > 1) {
            return parts.slice(1).map((p: string) => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
          }
          return 'Manual';
        }
        return provider.charAt(0).toUpperCase() + provider.slice(1) || 'Manual';
      }
    },
    { key: 'gateway_method', label: 'Gateway Method', render: (_, item: any) => getMethodLabel(item.gateway_method || item.payment_provider || item.payment_method) },
    { key: 'utr_number',       label: 'TransactionNo / UTR',    render: (v, item: any) => <span className="font-mono text-xs">{v || item.utr_number || item.utr_no || '—'}</span> },
    {
      key: 'status',
      label: 'Status',
      render: (val, item: any) => (
        <div className="flex flex-col gap-1">
          <Tag label={val} />
          {val === 'rejected' && item.reject_reason && (
            <span className="text-[10px] text-red-400 italic max-w-[150px] truncate block" title={item.reject_reason}>
              Reason: {item.reject_reason}
            </span>
          )}
        </div>
      )
    },
    { key: 'transaction_date', label: 'Date',   render: v => v ? new Date(v).toLocaleString() : '—' },
  ];

  // ── Columns: Withdrawals ────────────────────────────────────────────────────
  const withdrawalColumns: Column[] = [
    { key: 'id',               label: '#',      render: (_v, _r, i) => (page - 1) * limit + i + 1 },
    { key: 'amount',           label: 'Amount', render: (val) => <span className="font-bold text-white tabular-nums">{Number(val).toFixed(2)}</span> },
    { key: 'currency',         label: 'Currency', render: (_, item: any) => <span className="font-semibold text-slate-300">{(item.currency || item.transaction_type || 'INR').toUpperCase()}</span> },
    { key: 'gateway_name',     label: 'Method', render: (val) => <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 text-xs font-semibold">{getGatewayDisplayLabel(val)}</span> },
    { key: 'withdraw_method', label: 'Withdrawal Details', render: (_, item: any) => {
      let fields: { [key: string]: any } = {};
      try {
        const details = typeof item.payment_details === 'string'
          ? JSON.parse(item.payment_details)
          : item.payment_details;
        if (details && details.extra_fields) {
          fields = details.extra_fields;
        }
      } catch (e) {}

      const keys = Object.keys(fields).filter(k => k !== 'currency');
      if (keys.length > 0) {
        return (
          <div className="flex flex-col gap-0.5 text-[11px] text-slate-300 font-mono">
            {keys.map(k => (
              <div key={k} className="truncate max-w-[240px]" title={`${k.replace(/_/g, ' ').toUpperCase()}: ${fields[k]}`}>
                <span className="font-semibold text-indigo-400">{k.replace(/_/g, ' ').toUpperCase()}:</span> {fields[k]}
              </div>
            ))}
          </div>
        );
      }

      return (
        <div className="max-w-xs truncate text-xs text-slate-300 font-mono" title={item.withdraw_method}>
          {item.withdraw_method || 'N/A'}
        </div>
      );
    } },
    { key: 'utr_number',       label: 'Transaction ID / UTR',    render: (v, item: any) => <span className="font-mono text-xs">{v || item.utr_number || item.utr_no || '—'}</span> },
    {
      key: 'status',
      label: 'Status',
      render: (val, item: any) => (
        <div className="flex flex-col gap-1">
          <Tag label={val} />
          {val === 'rejected' && item.reject_reason && (
            <span className="text-[10px] text-red-400 italic max-w-[150px] truncate block" title={item.reject_reason}>
              Reason: {item.reject_reason}
            </span>
          )}
        </div>
      )
    },
    { key: 'transaction_date', label: 'Date',   render: v => v ? new Date(v).toLocaleString() : '—' },
  ];

  // ── Columns: Bets ─────────────────────────────────────────────────────────
  const betColumns: Column[] = [
    { key: 'id',         label: '#',         render: (_v, _r, i) => (page - 1) * limit + i + 1 },
    { key: 'txn_id',     label: 'Txn ID',    render: v => <span className="font-mono text-xs">{v || '—'}</span> },
    { key: 'amount',     label: 'Amount',    render: v => <span className="font-bold text-white tabular-nums">{v}</span> },
    { key: 'type',       label: 'Type',      render: v => <Tag label={v} /> },
    { key: 'round_id',   label: 'Round ID',  render: v => <span className="font-mono text-xs">{v || '—'}</span> },
    { key: 'game_id',    label: 'Game',      render: v => v ?? '—' },
    { key: 'is_cancel',  label: 'Cancelled', render: v => <Tag label={v === 1 ? 'yes' : 'no'} /> },
    { key: 'created_at', label: 'Date',      render: v => v ? new Date(v).toLocaleString() : '—' },
  ];

  const activeColumns =
    activeTab === 'bets' ? betColumns :
    activeTab === 'withdrawals' ? withdrawalColumns :
    depositColumns;

  // ── Loading / Error states ─────────────────────────────────────────────────
  if (isLoading && !data) return (
    <div className="ml-0 lg:ml-[256px] min-h-screen bg-[#0f1117] text-slate-100 animate-pulse">
      {/* Hero Banner Skeleton */}
      <div className="relative overflow-hidden border-b border-slate-800 bg-slate-900/30">
        <div className="relative px-8 pt-8 pb-6">
          {/* Breadcrumb skeleton */}
          <div className="h-4 w-32 bg-slate-800 rounded mb-5" />

          {/* Profile header row skeleton */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="w-20 h-20 rounded-2xl bg-slate-850" />
            <div className="flex-1 space-y-2.5">
              <div className="h-7 w-48 bg-slate-800 rounded" />
              <div className="flex gap-3 items-center">
                <div className="h-4 w-36 bg-slate-800 rounded" />
                <div className="h-3 w-px bg-slate-800" />
                <div className="h-4 w-20 bg-slate-800 rounded" />
                <div className="h-3 w-px bg-slate-800" />
                <div className="h-4 w-28 bg-slate-800 rounded" />
              </div>
            </div>
            <div className="h-9 w-24 bg-slate-800 rounded-lg" />
          </div>
        </div>
      </div>

      {/* Main Body Skeleton */}
      <div className="px-8 py-6 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* Left sidebar skeleton */}
        <div className="space-y-4">
          <div className="bg-slate-900/50 border border-slate-800/80 rounded-xl overflow-hidden p-4 space-y-3">
            <div className="h-3 w-24 bg-slate-800 rounded mb-2" />
            <div className="space-y-2">
              <div className="flex justify-between">
                <div className="h-3 w-16 bg-slate-800 rounded" />
                <div className="h-3 w-28 bg-slate-800 rounded" />
              </div>
              <div className="flex justify-between border-t border-slate-800/40 pt-2">
                <div className="h-3 w-16 bg-slate-800 rounded" />
                <div className="h-3 w-28 bg-slate-800 rounded" />
              </div>
              <div className="flex justify-between border-t border-slate-800/40 pt-2">
                <div className="h-3 w-20 bg-slate-800 rounded" />
                <div className="h-3 w-20 bg-slate-800 rounded" />
              </div>
            </div>
          </div>
        </div>

        {/* Right panel skeleton */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
          {/* Tabs header */}
          <div className="flex border-b border-slate-800 px-4 bg-slate-950/30 gap-6 h-12 items-center">
            <div className="h-4 w-16 bg-slate-800 rounded" />
            <div className="h-4 w-20 bg-slate-800 rounded" />
            <div className="h-4 w-12 bg-slate-800 rounded" />
            <div className="h-4 w-12 bg-slate-800 rounded" />
          </div>
          {/* Table placeholder */}
          <div className="p-4 space-y-3">
            <div className="h-8 bg-slate-800/40 rounded w-full" />
            <div className="h-10 bg-slate-800/20 rounded w-full" />
            <div className="h-10 bg-slate-800/20 rounded w-full" />
            <div className="h-10 bg-slate-800/20 rounded w-full" />
            <div className="h-10 bg-slate-800/20 rounded w-full" />
            <div className="h-10 bg-slate-800/20 rounded w-full" />
          </div>
        </div>
      </div>
    </div>
  );

  if (!profile) return (
    <div className="ml-0 lg:ml-[256px] min-h-screen bg-[#0f1117] flex items-center justify-center">
      <div className="text-center space-y-3">
        <p className="text-red-400 font-semibold">User not found.</p>
        <button onClick={() => navigate(-1)} className="text-indigo-400 text-sm hover:underline">← Back</button>
      </div>
    </div>
  );

  const pnl = parseFloat(profile.total_deposit) - parseFloat(profile.total_withdrawal);
  const initials = profile.name?.split(' ').map((w: any) => w[0]).join('').toUpperCase().slice(0, 2) || 'U';

  return (
    <div className="ml-0 lg:ml-[256px] min-h-screen bg-[#0f1117] text-slate-100">

      {/* ── HERO BANNER ──────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-950/80 via-[#0f1117] to-violet-950/60" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-indigo-600/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-0 right-1/4 w-64 h-64 bg-violet-600/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative px-8 pt-8 pb-6">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mb-5 text-xs text-slate-600">
            <button onClick={() => navigate(-1)} className="hover:text-slate-300 transition-colors flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
              Users
            </button>
            <span>/</span>
            <span className="text-slate-300 font-medium">{profile.name}</span>
          </div>

          {/* Profile header row */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="relative">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-2xl font-black text-white shadow-2xl shadow-indigo-500/20">
                {initials}
              </div>
              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full border-2 border-[#0f1117]" />
            </div>

            <div className="flex-1">
              <h1 className="text-2xl font-extrabold text-white tracking-tight">{profile.name}</h1>
              <div className="flex flex-wrap items-center gap-3 mt-1.5">
                <span className="text-slate-400 text-sm">{profile.email}</span>
                <span className="h-3 w-px bg-slate-700" />
                <span className="font-mono text-xs text-slate-500 bg-slate-800/80 px-2 py-0.5 rounded border border-slate-700">UID: {profile.id}</span>
                {profile.created && <>
                  <span className="h-3 w-px bg-slate-700" />
                  <span className="text-slate-500 text-xs">Joined {new Date(profile.created).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>
                </>}
              </div>
            </div>

            <button onClick={() => refetch()} className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-sm text-slate-300 font-medium transition-all">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
              Refresh
            </button>
          </div>



        </div>
      </div>

      {/* ── MAIN BODY ─────────────────────────────────────────────────────── */}
      <div className="px-8 py-6 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">

        {/* Left sidebar */}
        <div className="space-y-4">
          {/* Account Details */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-800">
              <h3 className="text-[10px] font-extrabold text-slate-500 uppercase tracking-widest">Account Details</h3>
            </div>
            <div className="divide-y divide-slate-800/50">
              {[
                { label: 'IP Address',    value: profile.ip_address || '—' },
                { label: 'Location',      value: profile.geo || '—' },
                { label: 'Referral Code', value: profile.referalcode || '—' },
              ].map(row => (
                <div key={row.label} className="px-4 py-3 flex justify-between gap-3">
                  <span className="text-xs text-slate-600 shrink-0">{row.label}</span>
                  <span className="text-xs text-slate-200 font-mono font-medium break-all text-right">{row.value}</span>
                </div>
              ))}
            </div>
          </div>


        </div>

        {/* Right panel — StakeTable */}
        <div className="flex flex-col gap-0 bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">

          {/* Main tabs */}
          <div className="flex border-b border-slate-800 px-1 bg-slate-950/30">
            {['Deposits','Withdrawals','Bets','Bonus'].map(tab => (
              <button
                key={tab}
                onClick={() => { setActiveTab(tab.toLowerCase()); setPage(1); setSubTab('all'); }}
                className={`relative px-5 py-3.5 text-sm font-semibold transition-all ${
                  activeTab === tab.toLowerCase() ? 'text-white' : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                {tab}
                {activeTab === tab.toLowerCase() && (
                  <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-indigo-500 rounded-full" />
                )}
              </button>
            ))}
          </div>

          {/* Sub-tabs (only for deposit/withdrawal) */}
          {(activeTab === 'deposits' || activeTab === 'withdrawals') && (
            <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-slate-800">
              {['all','pending','accepted','completed','partially_paid','rejected'].map(s => (
                <button key={s} onClick={() => { setSubTab(s); setPage(1); }}
                  className={`px-3 py-1 rounded-md text-xs font-semibold capitalize transition-all ${
                    subTab === s ? 'bg-indigo-600 text-white shadow-sm shadow-indigo-500/30' : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800'
                  }`}
                >
                  {s.replace('_', ' ')}
                </button>
              ))}
            </div>
          )}

          {/* ── StakeTable (reused component) ── */}
          <div className="p-4">
            <StakeTable
              title=""
              columns={activeColumns}
              data={txData?.data ?? []}
              isLoading={isFetching}
              hasSidebarMargin={false}
              emptyMessage="No records found for this tab."
              page={page}
              totalPages={totalPages}
              total={txData?.total ?? 0}
              limit={limit}
              onPageChange={setPage}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
